import {SpellCheckManager} from 'substance'

class DWSpellCheckManager extends SpellCheckManager {

    /**
     * Overridden onDocumentChange handler.
     * Handles line breaks, and updated and created elements, instead
     * of just handling updated elements as the base class does.
     *
     * @param change
     * @param info
     * @private
     */
    _onDocumentChange(change, info) {

        if (info.spellcheck) {
            return
        }

        // Note: instead of analyzing the model, we consider
        // all existing TextPropertyComponents instead
        // as this reflects what is presented to the user

        const textProperties = this.textPropertyManager._textProperties

        // When pressing return, check the next to last text property
        if (info.action === 'break') {
            const textProps = Object.keys(textProperties)
            let path = textProps[textProps.length - 2]

            this._runSpellCheck(path)
        }

        Object.keys(change.updated).forEach((pathStr) => {
            if (textProperties[pathStr]) {
                this._scheduleCheck(pathStr)
            }
        })

        Object.keys(change.created).forEach((pathStr) => {
            const textPath = `${pathStr},content`
            if (textProperties[textPath]) {
                this._runSpellCheck(textPath)
            }
        })
    }

    /**
     * Overridden spellcheck API call. Uses fetch and returns the request.
     * Run spellcheck for given content path
     *
     * @param {string} pathStr
     * @returns {*}
     * @private
     */
    _runSpellCheck(pathStr) {
        let path = pathStr.split(',')
        let text = this.editorSession.getDocument().get(path)
        let lang = this.editorSession.getLanguage()

        if (!text) {
            if (typeof text !== 'string') {
                return Promise.resolve([])
            }
            return this._clearSpellingErrorMarkers(this.editorSession.getDocument(), pathStr)
        }

        return fetch(this.apiURL, {
            method: 'POST',
            body: JSON.stringify({
                text,
                lang
            }),
            headers: {
                'Content-Type': 'application/json; charset=UTF-8'
            }
        })
            .then(res => res.json())
            .then((data) => {
                this._addSpellErrors(path, data)
            })
            .catch(function(err) {
                console.error(err)
            })
    }

    /**
     * Called when spell corrections have been returned.
     * Removes all spell errors on the given path first.
     *
     * @param path
     * @param data
     * @private
     */
    _addSpellErrors(path, data) {

        const doc = this.editorSession.getDocument()
        const oldErrors = doc.getIndex('markers').get(path).filter((marker) => {
            return marker.type === 'spell-error'
        })

        const newErrors = data.map(function(m) {
            return {
                type: 'spell-error',
                start: {
                    path: path,
                    offset: m.start
                },
                end: {
                    offset: m.end
                },
                suggestions: m.suggestions
            }
        })

        this.editorSession.transaction((tx) => {
            // remove the old markers first
            oldErrors.forEach((spellError) => {
                tx.delete(spellError.id)
            })
            // then add the new ones
            newErrors.forEach((spellError) => {
                tx.create(spellError)
            })
        }, {history: false, spellcheck: true})
    }

    /**
     * Clear all error markers, if any exists, from specified path
     *
     * @param {object} doc
     * @param {string} path
     */
    _clearSpellingErrorMarkers(doc, path) {
        const oldErrors = doc.getIndex('markers').get(path).filter((marker) => {
            return marker.type === 'spell-error'
        })

        this.editorSession.transaction((tx) => {
            oldErrors.forEach((spellError) => {
                tx.delete(spellError.id)
            })
        }, {history: false, spellcheck: true})
    }
}

export {DWSpellCheckManager}
